import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useTrack } from '../../hooks/useTrack.js';
import type { BeforeInstallPromptEvent } from './installPrompt.js';
import { usePwaEnvironment } from './usePwaEnvironment.js';

const DISMISSED_KEY = 'nutians:pwa-install-dismissed';
const VISITED_KEY = 'nutians:pwa-install-visited';
const INSTALLED_EVENT_KEY = 'nutians:pwa-installed-event-sent';

function readStorageFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeStorageFlag(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Install coaching is optional; storage errors must not affect the app.
  }
}

export function PwaInstallCoach() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const track = useTrack();
  const environment = usePwaEnvironment();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(() => readStorageFlag(DISMISSED_KEY));
  const [hasReturnVisit] = useState(() => readStorageFlag(VISITED_KEY));
  const [hasTrackedShown, setHasTrackedShown] = useState(false);

  useEffect(() => {
    if (!environment?.isStandalone || readStorageFlag(INSTALLED_EVENT_KEY)) return;
    writeStorageFlag(INSTALLED_EVENT_KEY);
    void track('pwa_installed', {
      browser: environment.browserKind,
      platform: environment.isIos ? 'ios' : environment.isAndroid ? 'android' : 'desktop',
      sourceRoute: location.pathname,
      standalone: true,
    });
  }, [environment, location.pathname, track]);

  useEffect(() => {
    if (hasReturnVisit) return;
    writeStorageFlag(VISITED_KEY);
  }, [hasReturnVisit]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  const sheetKind = useMemo(() => {
    if (!environment || isDismissed || environment.isStandalone) return null;
    if (environment.browserKind === 'ios-in-app' || environment.browserKind === 'ios-unsupported') {
      return 'openInSafari' as const;
    }
    if (environment.browserKind === 'ios-safari' && hasReturnVisit) return 'iosInstall' as const;
    if (environment.browserKind === 'android' && deferredPrompt) return 'androidInstall' as const;
    return null;
  }, [deferredPrompt, environment, hasReturnVisit, isDismissed]);

  useEffect(() => {
    if (!sheetKind || hasTrackedShown || !environment) return;
    setHasTrackedShown(true);
    void track(sheetKind === 'openInSafari' ? 'open_in_safari_prompted' : 'install_help_shown', {
      browser: environment.browserKind,
      platform: environment.isIos ? 'ios' : environment.isAndroid ? 'android' : 'desktop',
      sourceRoute: location.pathname,
      standalone: environment.isStandalone,
    });
  }, [environment, hasTrackedShown, location.pathname, sheetKind, track]);

  const dismiss = useCallback(() => {
    writeStorageFlag(DISMISSED_KEY);
    setIsDismissed(true);
  }, []);

  const copyLink = useCallback(async () => {
    try {
      await window.navigator.clipboard?.writeText(window.location.href);
    } catch {
      // The visible instruction remains useful when the Clipboard API is unavailable.
    }
  }, []);

  const installAndroid = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (!sheetKind) return null;

  const isSafariPath = sheetKind === 'openInSafari';
  const title = isSafariPath
    ? t('pwa.install.openInSafari.title')
    : sheetKind === 'androidInstall'
      ? t('pwa.install.android.title')
      : t('pwa.install.ios.title');
  const body = isSafariPath
    ? t('pwa.install.openInSafari.body')
    : sheetKind === 'androidInstall'
      ? t('pwa.install.android.body')
      : t('pwa.install.ios.body');

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:px-4 sm:pb-4">
      <section
        aria-labelledby="pwa-install-title"
        className="mx-auto max-w-xl rounded-lg border border-husk bg-shell p-4 text-ink shadow-xl dark:border-husk dark:bg-shell dark:text-ink"
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-husk text-lg text-leaf"
          >
            {isSafariPath ? '↗' : '↑'}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="pwa-install-title" className="text-base font-medium">
              {title}
            </h2>
            <p className="mt-1 text-sm text-ink-soft">{body}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="rounded border border-husk px-2 py-1 text-sm text-ink-soft hover:text-ink"
          >
            {t('dismiss')}
          </button>
        </div>

        {sheetKind === 'iosInstall' && (
          <ol className="mt-4 grid gap-2 text-sm text-ink sm:grid-cols-3">
            <li className="rounded border border-husk p-3">
              <span className="block text-xs font-medium text-leaf">
                {t('pwa.install.stepOneLabel')}
              </span>
              {t('pwa.install.ios.stepOne')}
            </li>
            <li className="rounded border border-husk p-3">
              <span className="block text-xs font-medium text-leaf">
                {t('pwa.install.stepTwoLabel')}
              </span>
              {t('pwa.install.ios.stepTwo')}
            </li>
            <li className="rounded border border-husk p-3">
              <span className="block text-xs font-medium text-leaf">
                {t('pwa.install.stepThreeLabel')}
              </span>
              {t('pwa.install.ios.stepThree')}
            </li>
          </ol>
        )}

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {isSafariPath && (
            <button
              type="button"
              onClick={() => void copyLink()}
              className="rounded border border-husk px-3 py-2 text-sm font-medium text-ink hover:bg-husk"
            >
              {t('pwa.install.copyLink')}
            </button>
          )}
          {sheetKind === 'androidInstall' && (
            <button
              type="button"
              onClick={() => void installAndroid()}
              className="rounded bg-leaf px-3 py-2 text-sm font-medium text-white hover:bg-leaf-deep"
            >
              {t('pwa.install.android.action')}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
